import React from 'react';
import { Edit, CheckCircle, XCircle, FileText, Loader } from 'lucide-react';
import { Can } from '../Auth/Can';
import './OrdersTable.css';

const OrdersTable = ({ 
  orders, 
  onOpenTicketModal, 
  onOpenPreviewModal, 
  generatingFactura = {}, 
  syncEventStatus = {} 
}) => {
  const calculateTotalCajas = (detalles) => {
    if (!detalles || detalles.length === 0) return 0;
    // Exclude Despacho products from box count
    return detalles.reduce((sum, detalle) => {
      if (detalle.productos?.category === 'Despacho') {
        return sum; // Skip Despacho products
      }
      return sum + detalle.cantidad_cajas;
    }, 0);
  };

  const getProductosSummary = (detalles) => {
    if (!detalles || detalles.length === 0) return 'Sin productos';
    return detalles
      .map((d) => `${d.productos?.nombre || 'Producto'} (${d.cantidad_cajas})`)
      .join(', ');
  };

  return (
    <div className="orders-table-container">
      <table className="orders-table">
        <thead>
          <tr>
            <th>Cliente</th>
            <th>Productos</th>
            <th>Total Cajas</th>
            <th>Ticket Despacho</th>
            <th>Factura Local</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const totalCajas = calculateTotalCajas(order.detalles);
            const hasTicket = order.ticket_despacho && order.ticket_despacho.trim() !== '';

            return (
              <tr key={order.id}>
                <td>
                  <div className="cliente-cell">
                    <div className="cliente-nombre">{order.clientes?.nombre || 'Sin cliente'}</div>
                    <div className="cliente-codigo">{order.clientes?.codigo_cliente || ''}</div>
                  </div>
                </td>
                <td>
                  <div className="productos-cell">{getProductosSummary(order.detalles)}</div>
                </td>
                <td>
                  <div className="cajas-cell">{totalCajas}</div>
                </td>
                <td>
                  <div className="ticket-cell">
                    {hasTicket ? (
                      <div className="ticket-badge con-ticket">
                        <CheckCircle size={16} />
                        <span>{order.ticket_despacho}</span>
                      </div>
                    ) : (
                      <div className="ticket-badge sin-ticket">
                        <XCircle size={16} />
                        <span>Sin Ticket</span>
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <div className="factura-local-cell">
                    {generatingFactura[order.id] || syncEventStatus[order.id]?.status === 'inprogress' ? (
                      <div className="factura-status generando">
                        <Loader size={16} className="spinner" />
                        <span>Generando...</span>
                      </div>
                    ) : syncEventStatus[order.id]?.status === 'success' && syncEventStatus[order.id]?.result?.numero_factura ? (
                      <div className="factura-status generado">
                        <CheckCircle size={16} />
                        <div className="factura-info">
                          <span className="folio">Folio: {syncEventStatus[order.id].result.numero_factura}</span>
                          {syncEventStatus[order.id]?.result?.pdf_url && (
                            <a 
                              href={syncEventStatus[order.id].result.pdf_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="pdf-link"
                            >
                              <FileText size={14} />
                              Ver Documento
                            </a>
                          )}
                        </div>
                      </div>
                    ) : syncEventStatus[order.id]?.status === 'error' ? (
                      <div className="factura-status error">
                        <XCircle size={16} />
                        <span>Error</span>
                      </div>
                    ) : (
                      <span className="factura-status sin-generar">Sin Generar</span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="actions-cell">
                    {!generatingFactura[order.id] && !syncEventStatus[order.id]?.result?.numero_factura && (
                      <Can I="manage" a="despacho-nacionales:manage-tickets">
                        <button
                          className="btn-action btn-preview"
                          onClick={() => onOpenPreviewModal(order)}
                          title="Ver datos y generar Factura Local"
                        >
                          <FileText size={16} />
                          Ver Datos y Generar
                        </button>
                      </Can>
                    )}
                    <Can I="manage" a="despacho-nacionales:manage-tickets">
                      <button
                        className="btn-action"
                        onClick={() => onOpenTicketModal(order)}
                        title={hasTicket ? 'Editar ticket' : 'Ingresar ticket'}
                      >
                        <Edit size={16} />
                        {hasTicket ? 'Editar' : 'Ingresar Ticket'}
                      </button>
                    </Can>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="orders-table-mobile">
        {orders.map((order) => {
          const totalCajas = calculateTotalCajas(order.detalles);
          const hasTicket = order.ticket_despacho && order.ticket_despacho.trim() !== '';

          return (
            <div key={order.id} className="order-card">
              <div className="order-card-header">
                <div className="cliente-info">
                  <div className="cliente-nombre">{order.clientes?.nombre || 'Sin cliente'}</div>
                  <div className="cliente-codigo">{order.clientes?.codigo_cliente || ''}</div>
                </div>
                {hasTicket ? (
                  <div className="ticket-badge con-ticket">
                    <CheckCircle size={16} />
                    <span>{order.ticket_despacho}</span>
                  </div>
                ) : (
                  <div className="ticket-badge sin-ticket">
                    <XCircle size={16} />
                    <span>Sin Ticket</span>
                  </div>
                )}
              </div>

              <div className="order-card-body">
                <div className="order-field">
                  <span className="field-label">Productos:</span>
                  <span className="field-value">{getProductosSummary(order.detalles)}</span>
                </div>
                <div className="order-field">
                  <span className="field-label">Total Cajas:</span>
                  <span className="field-value">{totalCajas}</span>
                </div>
              </div>

              <div className="order-card-actions">
                <Can I="manage" a="despacho-nacionales:manage-tickets">
                  <button
                    className="btn-action"
                    onClick={() => onOpenTicketModal(order)}
                  >
                    <Edit size={16} />
                    {hasTicket ? 'Editar Ticket' : 'Ingresar Ticket'}
                  </button>
                </Can>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrdersTable;
